import { useState } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragEndEvent,
} from '@dnd-kit/core';
import { ProjectKanbanColumn } from './ProjectKanbanColumn';
import { ProjectKanbanCard } from './ProjectKanbanCard';
import type { PipelineStatus } from '@/hooks/usePipelines';
import type { Project } from '@/hooks/useProjects';
import { useUpdateProjectStatus } from '@/hooks/useProjects';

interface ProjectKanbanBoardProps {
  statuses: PipelineStatus[];
  projects: Project[];
  onAddProject?: (statusId: string) => void;
}

export function ProjectKanbanBoard({ statuses, projects, onAddProject }: ProjectKanbanBoardProps) {
  const updateStatus = useUpdateProjectStatus();
  const [activeProject, setActiveProject] = useState<Project | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  const projectsByStatus = (statusId: string) =>
    projects.filter(p => p.current_status_id === statusId);

  const handleDragStart = (event: DragStartEvent) => {
    const project = projects.find(p => p.id === event.active.id);
    setActiveProject(project ?? null);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveProject(null);

    if (!over) return;

    // `over` can be a column (status id) or a card (project id)
    const targetStatusId = statuses.find(s => s.id === over.id)?.id
      ?? projects.find(p => p.id === over.id)?.current_status_id;

    if (!targetStatusId) return;

    const movingProject = projects.find(p => p.id === active.id);
    if (!movingProject || movingProject.current_status_id === targetStatusId) return;

    updateStatus.mutate({ id: movingProject.id, statusId: targetStatusId });
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="flex gap-4 overflow-x-auto pb-4">
        {statuses.map(status => (
          <ProjectKanbanColumn
            key={status.id}
            status={status}
            projects={projectsByStatus(status.id)}
            onAddProject={onAddProject}
          />
        ))}

        {statuses.length === 0 && (
          <div className="flex-1 flex items-center justify-center py-16 text-muted-foreground">
            No pipeline stages configured. Go to Settings → Projects to add stages.
          </div>
        )}
      </div>

      <DragOverlay>
        {activeProject ? <ProjectKanbanCard project={activeProject} /> : null}
      </DragOverlay>
    </DndContext>
  );
}
